import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Modal from 'react-modal';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'query-string';

import Button from '../../../global/Button';
import Angle from '../../../global/Angle';
import { getAsset } from '../../../../util/assets';
import getConfig from '../../../../util/config';
import useVideoSlideshowPlayback from './hooks/useVideoSlideshowPlayback';
import { ALBUM_SLIDESHOW_VIDEO_LAYOUT } from '../../../../constants/albums';
import sleep from '../../../../util/sleep';
import PlayButtonIcon from '../../../icons/PlayButtonIcon';
import PauseButtonIcon from '../../../icons/PauseButtonIcon';

export const LOAD_MORE_THRESHOLD = 5;

export const DISPLAY_MODE = {
  PRESENT: 'PRESENT',
  PREVIEW: 'PREVIEW',
};

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    logo_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  recordId: PropTypes.string,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      video: PropTypes.shape({
        id: PropTypes.string,
      }).isRequired,
    }),
  ),
  recordsTotal: PropTypes.number,
  loadMore: PropTypes.func,
  loadingMore: PropTypes.bool,
  shouldLoadMore: PropTypes.bool,
  renderTitle: PropTypes.func,
  actions: PropTypes.func,
  gridPath: PropTypes.string,
  dismissible: PropTypes.bool,
  onDismiss: PropTypes.func,
  includeWaldoLogo: PropTypes.bool,
  displayMode: PropTypes.oneOf(Object.values(DISPLAY_MODE)),
};

const VideoSlideshowGridViewer = ({
  album,
  records,
  recordId,
  recordsTotal,
  shouldLoadMore,
  loadMore,
  loadingMore,
  renderTitle,
  actions,
  gridPath,
  dismissible = true,
  onDismiss,
  includeWaldoLogo = true,
  displayMode = DISPLAY_MODE.PREVIEW,
  videosLayout = ALBUM_SLIDESHOW_VIDEO_LAYOUT.LANDSCAPE,
}) => {
  const match = useRouteMatch();
  const history = useHistory();
  const [hideMenus, setHideMenus] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [flipVideo, setFlipVideo] = useState(false);
  const hideMenusTimer = useRef();
  const showControlsTimer = useRef();
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const isPortrait = videosLayout === ALBUM_SLIDESHOW_VIDEO_LAYOUT.PORTRAIT;
  const config = getConfig();

  const onMouseMove = () => {
    setHideMenus(false);
    if (hideMenusTimer.current) {
      clearTimeout(hideMenusTimer.current);
      hideMenusTimer.current = null;
    }

    hideMenusTimer.current = setTimeout(() => setHideMenus(true), 2000);
  };

  const { url } = match;
  const basePath = url.split('/').slice(0, -1).join('/');

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.focus();
    // Trigger a mouse move. This ensures that the menu will be
    // hiding even if the user doesn't move the mouse at all.
    onMouseMove();

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      //  Clear the timeout if it exists by this point
      if (hideMenusTimer.current) {
        clearTimeout(hideMenusTimer.current);
        hideMenusTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (showControls) {
      if (showControlsTimer.current) {
        clearTimeout(showControlsTimer.current);
        showControlsTimer.current = null;
      }

      showControlsTimer.current = setTimeout(
        () => setShowControls(false),
        2000,
      );
    }
    return () => {
      if (showControlsTimer.current) {
        clearTimeout(showControlsTimer.current);
        showControlsTimer.current = null;
      }
    };
  }, [showControls]);

  const modalClasses = classNames({
    'slideshow-grid-viewer': true,
    'hide-until-move': hideMenus,
  });

  const headerClasses = classNames({
    'slideshow-grid-viewer-header': true,
    'hide-until-move': hideMenus,
  });

  const bodyClasses = classNames({
    'slideshow-grid-viewer-body': true,
    'full-until-move': hideMenus,
  });

  /**
   * Checks if more records are necessary to load and loads them
   * if we're approaching the end of the navigation (right-hand-wise)
   * so we would need to load more records to continue to the next record.
   *
   * @returns {Promise<void>}
   */
  const maybeLoadMoreRecords = async () => {
    if (loadingMore || !loadMore || !shouldLoadMore) {
      return;
    }

    const currentRecordIndex = records.findIndex(
      ({ video }) => recordId === video.id,
    );

    // If we're approaching the end and there are more records, load them
    // to keep a steady progression to the next record
    if (
      records.length - currentRecordIndex <= LOAD_MORE_THRESHOLD ||
      // If we land on a slide not loaded yet, we need to query stuff until we get it
      currentRecordIndex === -1
    ) {
      await loadMore();
    }
  };

  useEffect(() => {
    maybeLoadMoreRecords();
  }, [recordId, recordsTotal, records.length]);

  /**
   * Gets the adjacent record ID (ie previous or next one).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   * @returns {string|undefined}
   */
  const getAdjacentRecordId = adjacency => {
    const currentRecordIndex = records.findIndex(
      ({ video }) => recordId === video.id,
    );
    const nextRecord = records[currentRecordIndex + 1];
    const prevRecord = records[currentRecordIndex - 1];

    if (currentRecordIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecord) {
      return nextRecord.video.id;
    }

    if (adjacency === -1 && prevRecord) {
      return prevRecord.video.id;
    }

    return undefined;
  };

  /**
   * Gets the adjacent record index (ie previous or next one).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   * @returns {number|undefined}
   */
  const getAdjacentRecordIndex = adjacency => {
    const currentRecordIndex = records.findIndex(
      ({ video }) => recordId === video.id,
    );
    const nextRecord = currentRecordIndex + 1;
    const prevRecord = currentRecordIndex - 1;

    if (currentRecordIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecord < records.length) {
      return nextRecord;
    }

    if (adjacency === -1 && prevRecord > -1) {
      return prevRecord;
    }

    return undefined;
  };

  /**
   * Dismisses the Slide Viewer
   */
  const onDismissVideoSlideshow = () => {
    if (onDismiss) {
      onDismiss();
    } else {
      history.replace(gridPath);
    }
  };

  /**
   * Helper method that navigates to either the next or prev record
   * depending on whether they exist (with that priority) or dismisses
   * the slide viewer in case neither exist.
   */
  const onNavigateNextAfterRemoval = () => {
    const nextRecordId = getAdjacentRecordId(1);

    if (!nextRecordId) {
      const prevRecordId = getAdjacentRecordId(-1);

      if (!prevRecordId) {
        onDismissVideoSlideshow();
      } else {
        history.replace(`${basePath}/${prevRecordId}`);
      }

      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  const getVideoFromRecord = record => {
    if (!record) {
      return undefined;
    }

    return record.video;
  };

  const onChangeVideo = async ({ nextVideoId }) => {
    setFlipVideo(true);
    await sleep(1000);
    if (nextVideoId) {
      history.replace(`${basePath}/${nextVideoId}`);
    } else {
      history.replace(`${basePath.slice(0, -2)}/solicitation`);
    }
    await sleep(1000);
  };

  const record = records.find(({ video }) => recordId === video.id);
  const nextRecordId = getAdjacentRecordId(1);
  const nextRecordIdx = getAdjacentRecordIndex(1);
  const prevRecordId = getAdjacentRecordId(-1);
  const prevRecordIdx = getAdjacentRecordIndex(-1);

  const firstVideo = getVideoFromRecord(records[0]);
  const firstVideoUrl = `${firstVideo?.embed_url}&${qs.stringify({
    badge: 0,
    pip: 0,
    controls: 0,
    playsinline: 1,
    autoplay: 1,
  })}`;

  const nextVideo = getVideoFromRecord(
    records.find(({ video }) => nextRecordId === video.id),
  );
  const prevVideo = getVideoFromRecord(
    records.find(({ video }) => prevRecordId === video.id),
  );
  const video = getVideoFromRecord(record);
  const {
    isPlaying,
    onPlaybackStart,
    onPlaybackStop,
    onThumbnailClick,
    elapsedPercent,
    videoPlayerRef,
  } = useVideoSlideshowPlayback({
    videos: records,
    video,
    nextVideoId:
      displayMode === DISPLAY_MODE.PRESENT
        ? nextVideo?.id ?? records?.[0]?.video?.id
        : nextVideo?.id,
    onChangeVideo,
    onPlayVideo: async () => {
      await sleep(2500);
      setFlipVideo(false);
    },
  });

  // const videoOpacity = (() => {
  //   const fadeThreshold = 0.1;
  //   // Start fading out after 90% of the video is seen
  //   if (elapsedPercent - 0.9 > fadeThreshold) {
  //     return 1.0 - elapsedPercent;
  //   }
  //   // Fade in for the initial 10% of the video
  //   if (elapsedPercent <= fadeThreshold) {
  //     return elapsedPercent / fadeThreshold;
  //   }
  //   return 1.0;
  // })();

  const controlsClasses = classNames({
    'd-none': records?.length === 0,
    'slideshow-grid-viewer-video-menu-buttons': true,
    visible: showControls || !isPlaying,
  });

  const animatableClasses = classNames({
    'd-none': records?.length === 0,
    'slideshow-grid-viewer-animatable': true,
    flipped: flipVideo || elapsedPercent > 0.95,
  });

  const noVideosClasses = classNames({
    'd-none': records?.length > 0,
    'text-center text-white text-extra-bold': true,
  });

  return (
    <Modal
      ariaHideApp={false}
      isOpen
      bodyOpenClassName="body-modal-open"
      overlayClassName="slideshow-grid-viewer-overlay"
      className={modalClasses}
    >
      {includeWaldoLogo &&
        config?.marcomUrl &&
        (!authenticated || displayMode === DISPLAY_MODE.PRESENT) && (
          <a
            className="slideshow-grid-viewer-waldo-logo"
            href={config.marcomUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={getAsset(`/powered-by-waldo.svg`)} alt="Waldo Logo" />
          </a>
        )}
      {dismissible && (
        <Button
          btnStyle="reset"
          onClick={() => {
            onPlaybackStop();
            onDismissVideoSlideshow();
          }}
          className="video-slideshow-btn-dismiss text-lg"
        >
          <Angle direction="left" />
        </Button>
      )}
      <div className={headerClasses}>
        <div className="slideshow-grid-viewer-header-left" />

        <div className="slideshow-grid-viewer-header-center">
          {renderTitle && renderTitle()}
          {!renderTitle && (
            <h3 className="w-100 text-white text-center mb-0">{album.name}</h3>
          )}
        </div>

        {actions && (
          <div className="slideshow-grid-viewer-header-right">
            {actions({
              record,
              onPrevRecord: () =>
                onThumbnailClick({
                  slideId: prevRecordId,
                  slideIdx: prevRecordIdx,
                }),
              onNextRecord: () =>
                onThumbnailClick({
                  slideId: nextRecordId,
                  slideIdx: nextRecordIdx,
                }),
              onNavigateNextAfterRemoval,
              nextRecordId,
              prevRecordId,
              playbackActions: {
                onPlaybackStop,
                onPlaybackStart,
              },
            })}
          </div>
        )}
      </div>
      <div className={bodyClasses}>
        <div className="slideshow-grid-viewer-content">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <div
            role="button"
            tabIndex={0}
            className="slideshow-grid-viewer-video-menu"
            onClick={() => setShowControls(true)}
          >
            <div className={controlsClasses}>
              <Button
                btnStyle="reset"
                onClick={async () => {
                  onPlaybackStop();
                  await sleep(0.2);
                  await onChangeVideo({ nextVideoId: prevVideo?.id });
                }}
                disabled={!prevVideo || flipVideo || elapsedPercent > 0.95}
                className="mr-3"
              >
                <Angle direction="left" />
              </Button>
              <Button
                btnStyle="reset"
                disabled={flipVideo || elapsedPercent > 0.95}
                onClick={() => {
                  if (isPlaying) {
                    onPlaybackStop();
                  } else {
                    onPlaybackStart();
                  }
                }}
              >
                {isPlaying ? (
                  <PauseButtonIcon size={64} color="#fff" />
                ) : (
                  <PlayButtonIcon size={64} color="#fff" />
                )}
              </Button>
              <Button
                btnStyle="reset"
                onClick={async () => {
                  onPlaybackStop();
                  await sleep(0.2);
                  await onChangeVideo({ nextVideoId: nextVideo?.id });
                }}
                disabled={!nextVideo || flipVideo || elapsedPercent > 0.95}
                className="ml-3"
              >
                <Angle direction="right" />
              </Button>
            </div>
          </div>
          {/* Actively changing the src on the iframe impedes proper function from vimeo. */}
          <div className={animatableClasses}>
            <div className="slideshow-grid-viewer-flip-card">
              <iframe
                className={classNames({
                  portrait: isPortrait,
                  'slideshow-grid-viewer-flip-card-front': true,
                })}
                title={firstVideo?.name}
                src={firstVideoUrl}
                ref={videoPlayerRef}
                playsInline="1"
                allow="autoplay"
                allowFullScreen={false}
                webkitallowfullscreen={false}
                mozallowfullscreen={false}
              />
              <div className="slideshow-grid-viewer-flip-card-back" />
            </div>
          </div>
          <div className={noVideosClasses}>
            <img
              width="40px"
              src={getAsset(`/waldo-icon.svg`)}
              alt="Waldo"
              className="mb-2"
            />
            <p>No videos yet.</p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

VideoSlideshowGridViewer.propTypes = propTypes;

export default VideoSlideshowGridViewer;
